import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet } from '../lib/api';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';
import { EmptyState } from './ListView';
import ClaudeOutputModal from '../components/ClaudeOutputModal';

const PLANE_UI = process.env.REACT_APP_PLANE_UI || 'http://54.167.31.169:8083';
const PLANE_WORKSPACE = 'claude-skills';

function planeIssueUrl(task) {
  const project = task?.workspace_plane_project_identifier;
  const seq = task?.plane_issue_sequence_id;
  if (!project || !seq) return null;
  return `${PLANE_UI}/${PLANE_WORKSPACE}/browse/${project}-${seq}/`;
}

function PmCell({ task }) {
  const href = planeIssueUrl(task);
  if (!href) {
    return <span className="cell-muted">{task.plane_issue_id ? 'Synced (link pending)' : 'Not synced'}</span>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="pill-link pill-link--plane"
    >
      {task.workspace_plane_project_identifier}-{task.plane_issue_sequence_id} Open in Plane
    </a>
  );
}

function IntegrationsCell({ task }) {
  const badges = [];

  if (task.github_pr_url && task.github_pr_number) {
    badges.push(
      <a
        key="gh"
        href={task.github_pr_url}
        target="_blank"
        rel="noopener noreferrer"
        title={`GitHub PR #${task.github_pr_number}`}
        className="pill-link pill-link--github"
      >
        PR #{task.github_pr_number}
      </a>
    );
  }

  if (task.slack_thread_ts && task.slack_channel_id) {
    const p = `p${String(task.slack_thread_ts).replace('.', '')}`;
    const base = (process.env.REACT_APP_SLACK_WORKSPACE_URL || 'https://kyma-world.slack.com').replace(/\/$/, '');
    const href = `${base}/archives/${task.slack_channel_id}/${p}?thread_ts=${task.slack_thread_ts}&cid=${task.slack_channel_id}`;
    badges.push(
      <a
        key="slack"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title="Open Slack thread"
        className="pill-link pill-link--slack"
      >
        Slack thread
      </a>
    );
  } else if (task.slack_thread_ts) {
    badges.push(
      <span key="slack" title={`Slack thread ts: ${task.slack_thread_ts}`} className="pill-link pill-link--slack">
        Slack thread
      </span>
    );
  }

  if (badges.length === 0) return <span className="cell-muted">—</span>;
  return <span className="pill-stack">{badges}</span>;
}

function TaskOutputHost({ task, onClose }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [runs, setRuns] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    apiGet(`/tasks/${task.id}/runs`)
      .then((data) => {
        if (cancelled) return;
        setRuns(Array.isArray(data?.runs) ? data.runs : []);
        setSelectedIdx(0);
      })
      .catch((e) => {
        if (!cancelled) setErr(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [task.id]);

  return (
    <ClaudeOutputModal
      title={`#${task.id} · ${task.title}`}
      runs={runs}
      selectedIdx={selectedIdx}
      onSelectRun={setSelectedIdx}
      loading={loading}
      err={err}
      onClose={onClose}
    />
  );
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [outputTask, setOutputTask] = useState(null);

  const load = useCallback(async ({ silent = false, pull = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setErr('');
    }
    try {
      const path = pull || !silent ? '/tasks?pull=1' : '/tasks';
      const data = await apiGet(path);
      setTasks(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!silent) setErr(String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load({ pull: true }); }, [load]);

  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    const id = setInterval(() => { loadRef.current({ silent: true, pull: false }); }, 15000);
    return () => clearInterval(id);
  }, []);

  const filtered = filter === 'synced'
    ? tasks.filter((t) => !!t.plane_issue_id)
    : filter === 'unsynced'
      ? tasks.filter((t) => !t.plane_issue_id)
      : tasks;

  const syncedCount = tasks.filter((t) => !!t.plane_issue_id).length;

  return (
    <main className="page">
      <div className="page__header page__header--split">
        <div className="page__header-row">
          <span className="page__icon"><Icon name="tasks" /></span>
          <div className="page__header-text">
            <h2>Tasks</h2>
            <p>Task intakes with Plane, Slack, and Claude skill output.</p>
          </div>
        </div>
        <div className="page__header-actions">
          <span className="page__meta page__meta--inline">{syncedCount}/{tasks.length} synced to Plane</span>
          <button type="button" className="btn-secondary btn-small" onClick={() => load({ pull: true })} disabled={loading}>
            <Icon name="refresh" /> {loading ? 'Loading…' : 'Refresh'}
          </button>
          <a
            href={`${PLANE_UI}/${PLANE_WORKSPACE}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary btn-small"
          >
            <Icon name="external" /> Open Plane
          </a>
        </div>
      </div>

      {err && <div className="status status--error">{err}</div>}

      <section className="panel">
        <div className="filter-chips">
          {['all', 'synced', 'unsynced'].map((f) => (
            <button
              key={f}
              type="button"
              className={`filter-chip ${filter === f ? 'filter-chip--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? `All (${tasks.length})` : f === 'synced' ? `Synced (${syncedCount})` : `Not synced (${tasks.length - syncedCount})`}
            </button>
          ))}
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th>Title</th>
                <th style={{ width: '100px' }}>Workspace</th>
                <th style={{ width: '120px' }}>Status</th>
                <th style={{ width: '160px' }}>PM (Plane)</th>
                <th style={{ width: '140px' }}>Integrations</th>
                <th style={{ width: '110px' }}>Claude</th>
                <th style={{ width: '150px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="table-loading">Loading tasks…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8}><EmptyState label="No tasks found." /></td></tr>
              )}
              {!loading && filtered.map((t) => {
                const hasOut = t.has_claude_output === true || t.has_claude_output === 't';
                return (
                  <tr key={t.id} className={!t.plane_issue_id ? 'row-faded' : ''}>
                    <td className="cell-strong">#{t.id}</td>
                    <td className="cell-truncate">{t.title}</td>
                    <td className="cell-muted">{t.workspace_name || `ws-${t.workspace_id}`}</td>
                    <td><Badge value={t.status} /></td>
                    <td><PmCell task={t} /></td>
                    <td><IntegrationsCell task={t} /></td>
                    <td>
                      {hasOut || t.latest_run_id ? (
                        <button
                          type="button"
                          className={`btn-small ${hasOut ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setOutputTask(t)}
                        >
                          {hasOut ? 'View output' : 'View run'}
                        </button>
                      ) : (
                        <span className="cell-muted">—</span>
                      )}
                    </td>
                    <td className="cell-muted">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="panel__footnote">
          Plane CE running at <a href={PLANE_UI} target="_blank" rel="noopener noreferrer">{PLANE_UI}</a>
        </p>
      </section>

      {outputTask && (
        <TaskOutputHost task={outputTask} onClose={() => setOutputTask(null)} />
      )}
    </main>
  );
}
